import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Users, LogIn, FileText, UserCheck, Activity, Search,
  ShieldCheck, LogOut, RefreshCw, CalendarClock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "panel_token";

const panelApi = axios.create({ baseURL: API });
panelApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers["X-Panel-Token"] = t;
  return config;
});

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "-"; }
};

const MethodBadge = ({ method }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      method === "google" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
    }`}
    data-testid="user-method-badge"
  >
    {method === "google" ? "Google" : "E-posta / Şifre"}
  </span>
);

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value ?? "-"}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function PanelLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await panelApi.post("/panel/login", { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-slate-800 rounded-2xl p-8 shadow-2xl border border-slate-700"
        data-testid="panel-login-form"
      >
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center mb-3">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Yönetim Paneli</h1>
          <p className="text-sm text-slate-400 mt-1">Sadece yetkili erişim</p>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="panel-login-error">
            {error}
          </div>
        )}

        <label className="block text-sm text-slate-300 mb-1">Kullanıcı adı veya e-posta</label>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full mb-4 px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-600 text-white focus:border-amber-500 outline-none"
          autoComplete="username"
          data-testid="panel-username-input"
        />

        <label className="block text-sm text-slate-300 mb-1">Şifre</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-6 px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-600 text-white focus:border-amber-500 outline-none"
          autoComplete="current-password"
          data-testid="panel-password-input"
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors disabled:opacity-60"
          data-testid="panel-login-submit"
        >
          {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>
      </form>
    </div>
  );
}

function PanelDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logins, setLogins] = useState([]);
  const [tab, setTab] = useState("users");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, l] = await Promise.all([
        panelApi.get("/panel/stats"),
        panelApi.get("/panel/users"),
        panelApi.get("/panel/logins?limit=300"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setLogins(l.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">NKT Yönetim Paneli</h1>
              <p className="text-xs text-slate-400">Kullanıcı ve giriş yönetimi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm transition-colors"
              data-testid="panel-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-sm transition-colors"
              data-testid="panel-logout"
            >
              <LogOut className="w-4 h-4" /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Toplam Kullanıcı" value={stats?.total_users} accent="bg-blue-500" />
          <StatCard icon={Activity} label="Bugün Aktif" value={stats?.active_today} accent="bg-emerald-500" />
          <StatCard icon={CalendarClock} label="Bu Hafta Aktif" value={stats?.active_week} accent="bg-purple-500" />
          <StatCard icon={LogIn} label="Toplam Giriş" value={stats?.total_logins} accent="bg-amber-500" />
          <StatCard icon={UserCheck} label="Bugün Yeni Kayıt" value={stats?.new_today} accent="bg-teal-500" />
          <StatCard icon={UserCheck} label="Bu Hafta Yeni" value={stats?.new_week} accent="bg-cyan-500" />
          <StatCard icon={FileText} label="Toplam Teklif" value={stats?.total_proposals} accent="bg-orange-500" />
          <StatCard icon={Users} label="Toplam Müşteri" value={stats?.total_customers} accent="bg-pink-500" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setTab("users")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "users" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            data-testid="tab-users"
          >
            Kullanıcılar ({users.length})
          </button>
          <button
            onClick={() => setTab("logins")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "logins" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            data-testid="tab-logins"
          >
            Giriş Geçmişi ({logins.length})
          </button>
        </div>

        {tab === "users" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="relative max-w-sm">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="İsim veya e-posta ara..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-amber-500 outline-none"
                  data-testid="user-search"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Ad</th>
                    <th className="px-4 py-3 font-medium">E-posta</th>
                    <th className="px-4 py-3 font-medium">Telefon</th>
                    <th className="px-4 py-3 font-medium">Giriş Yöntemi</th>
                    <th className="px-4 py-3 font-medium">Kayıt Tarihi</th>
                    <th className="px-4 py-3 font-medium">Son Giriş</th>
                    <th className="px-4 py-3 font-medium text-center">Giriş</th>
                    <th className="px-4 py-3 font-medium text-center">Teklif</th>
                    <th className="px-4 py-3 font-medium text-center">Müşteri</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50" data-testid="user-row">
                      <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3 text-slate-600">{u.phone || "-"}</td>
                      <td className="px-4 py-3"><MethodBadge method={u.method} /></td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(u.last_login)}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{u.login_count}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{u.proposals}</td>
                      <td className="px-4 py-3 text-center text-slate-700">{u.customers}</td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400" data-testid="users-empty">Kullanıcı bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "logins" && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="logins-table">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Tarih / Saat</th>
                    <th className="px-4 py-3 font-medium">Ad</th>
                    <th className="px-4 py-3 font-medium">E-posta</th>
                    <th className="px-4 py-3 font-medium">Yöntem</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logins.map((e, i) => (
                    <tr key={i} className="hover:bg-slate-50" data-testid="login-row">
                      <td className="px-4 py-3 text-slate-700">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-3 text-slate-800">{e.name}</td>
                      <td className="px-4 py-3 text-slate-600">{e.email}</td>
                      <td className="px-4 py-3"><MethodBadge method={e.method} /></td>
                      <td className="px-4 py-3 text-slate-500">{e.ip}</td>
                    </tr>
                  ))}
                  {logins.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="logins-empty">
                      Henüz giriş kaydı yok. (Bu özellik eklendikten sonraki girişler burada listelenir.)
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function PanelPage() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setAuthed(false); return; }
    panelApi.get("/panel/me").then(() => setAuthed(true)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    });
  }, []);

  const logout = () => {
    panelApi.post("/panel/logout").catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return authed
    ? <PanelDashboard onLogout={logout} />
    : <PanelLogin onSuccess={() => setAuthed(true)} />;
}
